import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Ticket } from "lucide-react";
import { toast } from "sonner";
import { CarnetPanel } from "@/components/CarnetPanel";

const empty = { name: "", contact_person: "", email: "", phone: "", address: "", notes: "", language: "it" };

const LANG_FLAGS = { it: "🇮🇹", en: "🇬🇧", fr: "🇫🇷", es: "🇪🇸" };

export default function Clienti() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [carnetCust, setCarnetCust] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/customers");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      const payload = { ...form, email: form.email || null };
      if (editing) await api.put(`/customers/${editing.id}`, payload);
      else await api.post("/customers", payload);
      toast.success(editing ? "Cliente aggiornato" : "Cliente creato");
      setOpen(false);
      setEditing(null);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl">
      <PageHeader
        title="Clienti"
        description="Anagrafica delle strutture (ristoranti, wine bar, hotel) che ospitano i dispenser."
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-customer-btn" className="bg-brand hover:bg-brand-dark text-white rounded-sm">
                <Plus className="h-4 w-4 mr-2" /> Nuovo cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">
                  {editing ? "Modifica cliente" : "Nuovo cliente"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Nome / Ragione sociale</Label>
                  <Input data-testid="cust-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Referente</Label>
                    <Input data-testid="cust-contact" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="mt-1 rounded-sm" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Telefono</Label>
                    <Input data-testid="cust-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Email</Label>
                  <Input data-testid="cust-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Indirizzo</Label>
                  <Input data-testid="cust-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Lingua preferita</Label>
                  <Select value={form.language || "it"} onValueChange={(v) => setForm({ ...form, language: v })}>
                    <SelectTrigger data-testid="cust-language" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="it">🇮🇹 Italiano</SelectItem>
                      <SelectItem value="en">🇬🇧 English</SelectItem>
                      <SelectItem value="fr">🇫🇷 Français</SelectItem>
                      <SelectItem value="es">🇪🇸 Español</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="mt-1 text-[11px] text-zinc-500">Il tecnico vedrà una bandierina nel ticket per usare subito la lingua giusta nelle risposte WhatsApp.</div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Note</Label>
                  <Textarea data-testid="cust-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-sm" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annulla</Button>
                <Button data-testid="cust-save" className="bg-brand hover:bg-brand-dark text-white rounded-sm" onClick={save}>
                  Salva
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="rounded-sm border-zinc-200 p-0 overflow-hidden bg-white">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
              <th className="text-left px-5 py-3 font-medium">Cliente</th>
              <th className="text-left px-5 py-3 font-medium">Referente</th>
              <th className="text-left px-5 py-3 font-medium">Contatti</th>
              <th className="text-left px-5 py-3 font-medium">Indirizzo</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="p-10 text-center text-zinc-500">Nessun cliente ancora.</td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`cust-row-${c.id}`}>
                <td className="px-5 py-3 font-medium text-zinc-900">
                  <span className="mr-2" title={`Lingua: ${c.language || "it"}`} data-testid={`cust-lang-flag-${c.id}`}>{LANG_FLAGS[c.language || "it"] || "🇮🇹"}</span>
                  {c.name}
                </td>
                <td className="px-5 py-3 text-zinc-700">{c.contact_person || "—"}</td>
                <td className="px-5 py-3 text-zinc-700">
                  <div>{c.email || "—"}</div>
                  <div className="text-zinc-500 text-xs">{c.phone || ""}</div>
                </td>
                <td className="px-5 py-3 text-zinc-700">{c.address || "—"}</td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm"
                      data-testid={`cust-carnet-${c.id}`}
                      onClick={() => setCarnetCust(c)}
                      title="Gestisci carnet ore"
                    >
                      <Ticket className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm"
                      data-testid={`cust-edit-${c.id}`}
                      onClick={() => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>

      <Dialog open={!!carnetCust} onOpenChange={(v) => !v && setCarnetCust(null)}>
        <DialogContent className="rounded-sm max-w-xl" data-testid="carnet-cust-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Carnet · {carnetCust?.name}</DialogTitle>
          </DialogHeader>
          {carnetCust && <CarnetPanel customerId={carnetCust.id} userRole={user?.role} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
